/**
 * ============================================================================
 * USER / TENANT PAGE: IndiaMART CRM Leads Integration & WhatsApp Auto-Reply
 * ============================================================================
 * Purpose:
 *   Comprehensive Hope UI styled control panel for IndiaMART Lead Integration:
 *   - Per-user IndiaMART CRM API Key & Mobile number setup
 *   - Dynamic Inbound Webhook (Push API) endpoint generation with instant copy
 *   - Dynamic Automated "Thank You" WhatsApp messaging rules & variable placeholders
 *   - Real-time interactive WhatsApp message preview
 *   - Leads History table with search, filters, auto-reply status, and manual retry
 *   - Direct "Open Chat" bridge to Live Chat screen
 *   - User-wise permission enforcement (`INDIAMART_INTEGRATION`)
 * ============================================================================
 */

import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import {
  ArrowPathIcon,
  CheckCircleIcon,
  ClipboardDocumentIcon,
  ClipboardDocumentCheckIcon,
  ExclamationTriangleIcon,
  KeyIcon,
  PaperAirplaneIcon,
  PhoneIcon,
  ChatBubbleLeftRightIcon,
  MagnifyingGlassIcon,
  ArrowDownTrayIcon,
  InformationCircleIcon,
  SparklesIcon,
  ShieldCheckIcon,
  EyeIcon,
  EyeSlashIcon,
  XMarkIcon,
  ClockIcon,
  CheckIcon,
} from "@heroicons/react/24/outline";

import { indiaMartService, phoneNumberService, templateService } from "../../services";
import { getCurrentUser, canAccessIndiaMart } from "../../../core/utils/auth";

export default function IndiaMartIntegration() {
  const navigate = useNavigate();
  const currentUser = getCurrentUser();
  const hasAccess = canAccessIndiaMart(currentUser);

  // Active tab: "config" | "leads"
  const [activeTab, setActiveTab] = useState("config");

  // Loading states
  const [loadingConfig, setLoadingConfig] = useState(true);
  const [savingConfig, setSavingConfig] = useState(false);
  const [testingConnection, setTestingConnection] = useState(false);
  const [syncingLeads, setSyncingLeads] = useState(false);
  const [loadingLeads, setLoadingLeads] = useState(false);

  // Configuration state
  const [config, setConfig] = useState({
    str_glusr_crm_key: "",
    str_glusr_mobile: "",
    str_webhook_token: "",
    bool_is_active: true,
    bool_auto_reply_enabled: true,
    str_reply_mode: "TEXT",
    str_sender_phone_number: "",
    str_template_name: "",
    str_custom_message:
      "Hello {{name}}, thank you for contacting us on IndiaMART regarding {{product}}! We have received your inquiry and our team will get in touch with you shortly. For immediate assistance, reply to this message.",
    bool_prevent_duplicate_24h: true,
    webhookUrl: "",
    dttm_last_sync_on: null,
  });

  // Available WhatsApp phone numbers & templates
  const [phoneNumbers, setPhoneNumbers] = useState([]);
  const [templates, setTemplates] = useState([]);

  // Visibility toggle for API Key
  const [showApiKey, setShowApiKey] = useState(false);
  const [copiedWebhook, setCopiedWebhook] = useState(false);

  // Leads list and filter state
  const [leads, setLeads] = useState([]);
  const [leadsStats, setLeadsStats] = useState({
    totalAll: 0,
    totalSent: 0,
    totalFailed: 0,
    totalSkipped: 0,
  });
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("ALL");
  const [dateFilter, setDateFilter] = useState("");

  // Modals state
  const [selectedLead, setSelectedLead] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showManualModal, setShowManualModal] = useState(false);
  const [manualMessage, setManualMessage] = useState("");
  const [manualSending, setManualSending] = useState(false);

  // 1. Fetch Configuration & Phone numbers on mount
  const loadConfig = useCallback(async () => {
    try {
      setLoadingConfig(true);
      const res = await indiaMartService.getConfig();
      const data = res?.data || res;
      if (data && (data._id || data.webhookUrl || data.str_webhook_token || data.str_glusr_crm_key)) {
        setConfig((prev) => ({
          ...prev,
          ...data,
          webhookUrl: data.webhookUrl || prev.webhookUrl,
        }));
      }
    } catch (err) {
      console.error("Failed to load IndiaMART config:", err);
    } finally {
      setLoadingConfig(false);
    }
  }, []);

  const loadResources = useCallback(async () => {
    try {
      const [phonesRes, tmplRes] = await Promise.allSettled([
        phoneNumberService.get ? phoneNumberService.get({ limit: 100 }) : phoneNumberService.getPhoneNumbers(),
        templateService.get ? templateService.get({ limit: 500, str_status: "APPROVED" }) : templateService.getTemplates(),
      ]);

      if (phonesRes.status === "fulfilled" && phonesRes.value) {
        const val = phonesRes.value;
        const list = Array.isArray(val)
          ? val
          : Array.isArray(val.data)
          ? val.data
          : Array.isArray(val.data?.data)
          ? val.data.data
          : [];
        setPhoneNumbers(list);
      }

      if (tmplRes.status === "fulfilled" && tmplRes.value) {
        const val = tmplRes.value;
        const tmpls = Array.isArray(val)
          ? val
          : Array.isArray(val.data)
          ? val.data
          : Array.isArray(val.data?.data)
          ? val.data.data
          : [];
        // Filter specifically for APPROVED templates
        const approvedOnly = tmpls.filter(
          (t) => (t.str_status || t.status || "").toUpperCase() === "APPROVED"
        );
        setTemplates(approvedOnly.length > 0 ? approvedOnly : tmpls);
      }
    } catch (err) {
      console.warn("Failed to load helper resources:", err);
    }
  }, []);

  // 2. Fetch Leads
  const loadLeads = useCallback(
    async (p = 1) => {
      try {
        setLoadingLeads(true);
        const params = {
          page: p,
          limit: 15,
          search: searchQuery || undefined,
          status: statusFilter !== "ALL" ? statusFilter : undefined,
          startDate: dateFilter || undefined,
        };
        const res = await indiaMartService.getLeads(params);
        if (res.success) {
          setLeads(res.data || []);
          if (res.pagination) {
            setPage(res.pagination.page);
            setTotalPages(res.pagination.totalPages);
          }
          if (res.stats) {
            setLeadsStats(res.stats);
          }
        }
      } catch (err) {
        console.error("Failed to load IndiaMART leads:", err);
      } finally {
        setLoadingLeads(false);
      }
    },
    [searchQuery, statusFilter, dateFilter]
  );

  useEffect(() => {
    if (hasAccess) {
      loadConfig();
      loadResources();
    }
  }, [hasAccess, loadConfig, loadResources]);

  useEffect(() => {
    if (hasAccess && activeTab === "leads") {
      loadLeads(1);
    }
  }, [hasAccess, activeTab, loadLeads]);

  // Handle Save Configuration
  const handleSaveConfig = async (e) => {
    if (e) e.preventDefault();
    try {
      setSavingConfig(true);
      const res = await indiaMartService.saveConfig(config);
      const data = res?.data || res;
      if (res?.success || data?._id || data?.webhookUrl) {
        if (data && typeof data === "object") {
          setConfig((prev) => ({
            ...prev,
            ...data,
            webhookUrl: data.webhookUrl || prev.webhookUrl,
          }));
        }
        Swal.fire({
          icon: "success",
          title: "Settings Saved",
          text: "IndiaMART integration settings updated successfully!",
          timer: 2000,
          showConfirmButton: false,
          toast: true,
          position: "top-end",
        });
      }
    } catch (err) {
      Swal.fire({
        icon: "error",
        title: "Save Failed",
        text: err.response?.data?.error || err.message || "Failed to save settings",
      });
    } finally {
      setSavingConfig(false);
    }
  };

  // Handle Test Connection
  const handleTestConnection = async () => {
    if (!config.str_glusr_crm_key) {
      Swal.fire({
        icon: "warning",
        title: "API Key Required",
        text: "Please enter your IndiaMART CRM API Key before testing connection.",
      });
      return;
    }

    try {
      setTestingConnection(true);
      const res = await indiaMartService.testConnection({
        str_glusr_crm_key: config.str_glusr_crm_key,
      });

      if (res && res.success) {
        Swal.fire({
          icon: "success",
          title: "Connection Successful! 🎉",
          html: `<p class="text-sm text-gray-600">${res.message}</p>
                 <p class="text-xs text-gray-500 mt-2">Available records in CRM window: <b>${res.totalRecords || 0}</b></p>`,
        });
      } else {
        Swal.fire({
          icon: "error",
          title: "Connection Failed",
          text: res?.error || "Could not connect to IndiaMART CRM. Please check your CRM API Key.",
        });
      }
    } catch (err) {
      Swal.fire({
        icon: "error",
        title: "Connection Failed",
        text: err.response?.data?.error || err.message || "Could not connect to IndiaMART CRM.",
      });
    } finally {
      setTestingConnection(false);
    }
  };

  // Handle Manual Sync Leads Now
  const handleFetchLeadsNow = async () => {
    try {
      setSyncingLeads(true);
      const res = await indiaMartService.fetchLeadsNow();
      const data = res?.data || res;
      if (res?.success || data) {
        Swal.fire({
          icon: "success",
          title: "Synchronization Complete",
          html: `
            <div class="text-left text-sm space-y-1.5 p-2 bg-gray-50 rounded-lg">
              <div>📥 <b>Total Fetched:</b> ${data?.totalFetched ?? 0}</div>
              <div>✨ <b>New Leads:</b> ${data?.newLeads ?? 0}</div>
              <div>💬 <b>WhatsApp Sent:</b> ${data?.messagesSent ?? 0}</div>
              <div>⚠️ <b>Duplicates/Skipped:</b> ${(data?.duplicates || 0) + (data?.messagesSkipped || 0)}</div>
            </div>
          `,
        });
        loadConfig();
        if (activeTab === "leads") {
          loadLeads(1);
        }
      }
    } catch (err) {
      Swal.fire({
        icon: "error",
        title: "Sync Failed",
        text: err.response?.data?.error || err.message || "Failed to fetch leads from IndiaMART",
      });
    } finally {
      setSyncingLeads(false);
    }
  };

  // Handle Regenerate Webhook Token
  const handleRegenerateToken = async () => {
    const result = await Swal.fire({
      title: "Regenerate Webhook URL?",
      text: "This will invalidate the existing webhook URL. You must update the URL in IndiaMART Seller Panel.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3a57e8",
      confirmButtonText: "Yes, regenerate",
    });

    if (result.isConfirmed) {
      try {
        const res = await indiaMartService.regenerateToken();
        const data = res?.data || res;
        if (data && (data.token || data.webhookUrl || res?.success)) {
          setConfig((prev) => ({
            ...prev,
            str_webhook_token: data.token || prev.str_webhook_token,
            webhookUrl: data.webhookUrl || prev.webhookUrl,
          }));
          Swal.fire({
            icon: "success",
            title: "New Webhook URL Generated",
            text: "Please copy and paste this new URL in your IndiaMART CRM settings.",
          });
        }
      } catch (err) {
        Swal.fire({
          icon: "error",
          title: "Failed",
          text: err.response?.data?.error || err.message,
        });
      }
    }
  };

  // Copy Webhook to Clipboard
  const handleCopyWebhook = () => {
    if (!config.webhookUrl) return;
    navigator.clipboard.writeText(config.webhookUrl);
    setCopiedWebhook(true);
    setTimeout(() => setCopiedWebhook(false), 2500);
  };

  // Insert Variable Tag into Custom Message
  const handleInsertVariable = (tag) => {
    setConfig((prev) => ({
      ...prev,
      str_custom_message: (prev.str_custom_message || "") + ` {{${tag}}} `,
    }));
  };

  // Selected Template and its body content
  const selectedTemplate = useMemo(() => {
    if (!config.str_template_name) return null;
    return templates.find(
      (t) => (t.str_template_name || t.name) === config.str_template_name
    );
  }, [templates, config.str_template_name]);

  const selectedTemplateBody = useMemo(() => {
    if (!selectedTemplate) return "";
    let body = selectedTemplate.obj_message?.body || "";
    if (!body && Array.isArray(selectedTemplate.obj_message?.components)) {
      const comp = selectedTemplate.obj_message.components.find((c) => c.type === "BODY");
      body = comp?.text || "";
    }
    if (!body && Array.isArray(selectedTemplate.components)) {
      const comp = selectedTemplate.components.find((c) => c.type === "BODY");
      body = comp?.text || "";
    }
    return body;
  }, [selectedTemplate]);

  // Real-time Preview computation
  const previewMessage = useMemo(() => {
    let text = "";
    if (config.str_reply_mode === "TEMPLATE") {
      text =
        selectedTemplateBody ||
        (config.str_template_name
          ? `[Template: ${config.str_template_name}]`
          : "Please select an approved WhatsApp template...");
    } else {
      text = config.str_custom_message || "";
    }
    return text
      .replace(/{{\s*(name|1)\s*}}/gi, "Rahul Sharma")
      .replace(/{{\s*(phone|mobile|2)\s*}}/gi, "+91 98765 43210")
      .replace(/{{\s*(product|item|3)\s*}}/gi, "Commercial Ro Water Plant")
      .replace(/{{\s*(city|4)\s*}}/gi, "Ahmedabad")
      .replace(/{{\s*(company|5)\s*}}/gi, "Sharma Industries")
      .replace(/{{\s*(query_id|6)\s*}}/gi, "Q1048291");
  }, [config.str_reply_mode, config.str_custom_message, config.str_template_name, selectedTemplateBody]);

  // Open Manual WhatsApp Dispatch Modal
  const openManualSend = (lead) => {
    setSelectedLead(lead);
    setManualMessage(
      (config.str_custom_message || "")
        .replace(/{{\s*name\s*}}/gi, lead.str_sender_name || "Customer")
        .replace(/{{\s*phone\s*}}/gi, lead.str_sender_mobile || "")
        .replace(/{{\s*product\s*}}/gi, lead.str_product_name || lead.str_subject || "our products")
        .replace(/{{\s*city\s*}}/gi, lead.str_sender_city || "your area")
        .replace(/{{\s*company\s*}}/gi, lead.str_sender_company || "")
        .replace(/{{\s*query_id\s*}}/gi, lead.str_query_id || "")
    );
    setShowManualModal(true);
  };

  // Submit Manual WhatsApp Dispatch
  const submitManualSend = async () => {
    if (!selectedLead) return;
    try {
      setManualSending(true);
      const res = await indiaMartService.sendManualMessage(selectedLead._id, {
        message: manualMessage,
        senderPhone: config.str_sender_phone_number,
      });

      if (res.success) {
        Swal.fire({
          icon: "success",
          title: "Message Sent!",
          text: `WhatsApp message dispatched to ${selectedLead.str_sender_mobile}`,
          timer: 2000,
          showConfirmButton: false,
          toast: true,
          position: "top-end",
        });
        setShowManualModal(false);
        loadLeads(page);
      }
    } catch (err) {
      Swal.fire({
        icon: "error",
        title: "Dispatch Failed",
        text: err.response?.data?.error || err.message || "Could not send message",
      });
    } finally {
      setManualSending(false);
    }
  };

  // Export Leads to CSV
  const exportToCSV = () => {
    if (leads.length === 0) {
      Swal.fire({ icon: "info", title: "No Data", text: "No leads available to export." });
      return;
    }

    const headers = [
      "Query ID",
      "Date Time",
      "Buyer Name",
      "Mobile",
      "Email",
      "City",
      "State",
      "Product",
      "Requirement",
      "Reply Status",
      "Sent Message",
    ];

    const rows = leads.map((l) => [
      `"${l.str_query_id || ""}"`,
      `"${new Date(l.dttm_query_time).toLocaleString()}"`,
      `"${(l.str_sender_name || "").replace(/"/g, '""')}"`,
      `"${l.str_sender_mobile || ""}"`,
      `"${l.str_sender_email || ""}"`,
      `"${(l.str_sender_city || "").replace(/"/g, '""')}"`,
      `"${(l.str_sender_state || "").replace(/"/g, '""')}"`,
      `"${(l.str_product_name || "").replace(/"/g, '""')}"`,
      `"${(l.str_query_message || "").replace(/"/g, '""')}"`,
      `"${l.str_reply_status || ""}"`,
      `"${(l.str_reply_message || "").replace(/"/g, '""')}"`,
    ]);

    const csvContent =
      "data:text/csv;charset=utf-8," + [headers.join(","), ...rows.map((e) => e.join(","))].join("\n");

    const encodedUri = encodeURI(csvContent);
    const link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    link.setAttribute("download", `indiamart_leads_${new Date().toISOString().slice(0, 10)}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // If user does not have permission for IndiaMART
  if (!hasAccess) {
    return (
      <div className="min-h-[70vh] flex items-center justify-center p-6">
        <div className="max-w-md w-full bg-white dark:bg-gray-800 rounded-2xl shadow-xl p-8 text-center border border-gray-100 dark:border-gray-700">
          <div className="w-16 h-16 bg-amber-100 text-amber-600 rounded-2xl flex items-center justify-center mx-auto mb-4">
            <ExclamationTriangleIcon className="w-9 h-9" />
          </div>
          <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-2">
            Integration Permission Required
          </h2>
          <p className="text-sm text-gray-500 dark:text-gray-400 mb-6">
            IndiaMART Lead Integration is not currently enabled for your user account. Please contact
            your platform administrator to grant the <b>IndiaMART Integration</b> permission.
          </p>
          <button
            onClick={() => navigate("/")}
            className="px-5 py-2.5 bg-[#3a57e8] hover:bg-[#2b44c2] text-white rounded-xl text-sm font-semibold transition-all shadow-md shadow-[#3a57e8]/30"
          >
            Return to Dashboard
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 mx-auto">
      {/* ── Top Header Bar ── */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 bg-white dark:bg-gray-800 p-6 rounded-2xl shadow-sm border border-gray-100 dark:border-gray-700">
        <div className="flex items-center gap-4">
          <div className="w-13 h-13 rounded-2xl bg-gradient-to-tr from-[#3a57e8] to-[#607afb] flex items-center justify-center shadow-lg shadow-[#3a57e8]/30 text-white flex-shrink-0">
            <SparklesIcon className="w-7 h-7" />
          </div>
          <div>
            <div className="flex items-center gap-2.5">
              <h1 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white tracking-tight">
                IndiaMART Leads Integration
              </h1>
              <span
                className={`inline-flex items-center gap-1.5 px-3 py-0.5 rounded-full text-xs font-semibold ${
                  config.bool_is_active
                    ? "bg-emerald-50 text-emerald-700 border border-emerald-200 dark:bg-emerald-950/40 dark:text-emerald-400"
                    : "bg-gray-100 text-gray-600 border border-gray-200 dark:bg-gray-700 dark:text-gray-400"
                }`}
              >
                <span
                  className={`w-2 h-2 rounded-full ${
                    config.bool_is_active ? "bg-emerald-500 animate-pulse" : "bg-gray-400"
                  }`}
                />
                {config.bool_is_active ? "Integration Active" : "Disabled"}
              </span>
            </div>
            <p className="text-xs sm:text-sm text-gray-500 dark:text-gray-400 mt-0.5">
              Automate instant WhatsApp "Thank You" messages to buyer inquiries received on IndiaMART
            </p>
          </div>
        </div>

        {/* Action Header Buttons */}
        <div className="flex items-center flex-wrap gap-2.5">
          <button
            onClick={handleTestConnection}
            disabled={testingConnection}
            className="flex items-center gap-2 px-3.5 py-2 rounded-xl text-xs sm:text-sm font-semibold border border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 transition-all shadow-xs"
          >
            <ShieldCheckIcon
              className={`w-4 h-4 text-[#3a57e8] ${testingConnection ? "animate-spin" : ""}`}
            />
            {testingConnection ? "Pinging..." : "Test Connection"}
          </button>

          <button
            onClick={handleFetchLeadsNow}
            disabled={syncingLeads}
            className="flex items-center gap-2 px-4 py-2 rounded-xl text-xs sm:text-sm font-semibold text-white bg-[#3a57e8] hover:bg-[#2b44c2] transition-all shadow-md shadow-[#3a57e8]/30 cursor-pointer disabled:opacity-60"
          >
            <ArrowPathIcon className={`w-4 h-4 ${syncingLeads ? "animate-spin" : ""}`} />
            {syncingLeads ? "Syncing..." : "Fetch Leads Now"}
          </button>
        </div>
      </div>

      {/* ── Navigation Tabs ── */}
      <div className="flex border-b border-gray-200 dark:border-gray-700 space-x-2">
        <button
          onClick={() => setActiveTab("config")}
          className={`pb-3 px-4 text-sm font-semibold transition-colors relative cursor-pointer ${
            activeTab === "config"
              ? "text-[#3a57e8] border-b-2 border-[#3a57e8]"
              : "text-gray-500 hover:text-gray-800 dark:text-gray-400 dark:hover:text-white"
          }`}
        >
          ⚙️ Connection & Auto-Reply Rules
        </button>

        <button
          onClick={() => setActiveTab("leads")}
          className={`pb-3 px-4 text-sm font-semibold transition-colors relative cursor-pointer flex items-center gap-2 ${
            activeTab === "leads"
              ? "text-[#3a57e8] border-b-2 border-[#3a57e8]"
              : "text-gray-500 hover:text-gray-800 dark:text-gray-400 dark:hover:text-white"
          }`}
        >
          📋 Leads History & Audit Logs
          {leadsStats.totalAll > 0 && (
            <span className="px-2 py-0.5 rounded-full text-xs bg-[#3a57e8]/10 text-[#3a57e8] font-bold">
              {leadsStats.totalAll}
            </span>
          )}
        </button>
      </div>

      {/* ════════════════════════════════════════════════════════════════════════ */}
      {/* TAB 1: CONFIGURATION & AUTO-REPLY SETTINGS                               */}
      {/* ════════════════════════════════════════════════════════════════════════ */}
      {activeTab === "config" && (
        loadingConfig ? (
          <div className="bg-white dark:bg-gray-800 p-12 rounded-2xl border border-gray-100 dark:border-gray-700 text-center text-gray-400 shadow-sm">
            <ArrowPathIcon className="w-7 h-7 animate-spin mx-auto mb-3 text-[#3a57e8]" />
            <p className="text-sm font-semibold text-gray-600 dark:text-gray-300">
              Loading IndiaMART integration settings...
            </p>
          </div>
        ) : (
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          {/* Left Column: Form Settings (7 cols) */}
          <div className="lg:col-span-7 space-y-6">
            <form onSubmit={handleSaveConfig} className="space-y-6">
              {/* 1. IndiaMART CRM Credentials Card */}
              <div className="bg-white dark:bg-gray-800 p-6 rounded-2xl shadow-sm border border-gray-100 dark:border-gray-700 space-y-4">
                <div className="flex items-center justify-between pb-3 border-b border-gray-100 dark:border-gray-700">
                  <div className="flex items-center gap-2.5">
                    <KeyIcon className="w-5 h-5 text-[#3a57e8]" />
                    <h3 className="text-base font-bold text-gray-900 dark:text-white">
                      IndiaMART CRM API Credentials
                    </h3>
                  </div>

                  {/* Enable / Disable Switch */}
                  <label className="flex items-center gap-2 cursor-pointer">
                    <span className="text-xs font-medium text-gray-600 dark:text-gray-300">
                      Enable Integration
                    </span>
                    <input
                      type="checkbox"
                      checked={config.bool_is_active}
                      onChange={(e) =>
                        setConfig((prev) => ({ ...prev, bool_is_active: e.target.checked }))
                      }
                      className="sr-only peer"
                    />
                    <div className="w-10 h-6 bg-gray-200 peer-focus:outline-none rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[#3a57e8]" />
                  </label>
                </div>

                {/* CRM Key Input */}
                <div>
                  <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1.5">
                    GLUSR CRM API Key <span className="text-rose-500">*</span>
                  </label>
                  <div className="relative">
                    <input
                      type={showApiKey ? "text" : "password"}
                      value={config.str_glusr_crm_key}
                      onChange={(e) =>
                        setConfig((prev) => ({ ...prev, str_glusr_crm_key: e.target.value }))
                      }
                      placeholder="e.g. mRxxxxxxxxxxxxxxxxxxx=="
                      className="w-full px-3.5 py-2.5 rounded-xl border border-gray-200 dark:border-gray-700 bg-gray-50/50 dark:bg-gray-900/50 text-gray-900 dark:text-white text-sm focus:ring-2 focus:ring-[#3a57e8] focus:border-transparent transition-all pr-10 font-mono"
                    />
                    <button
                      type="button"
                      onClick={() => setShowApiKey(!showApiKey)}
                      className="absolute right-3 top-2.5 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                    >
                      {showApiKey ? <EyeSlashIcon className="w-5 h-5" /> : <EyeIcon className="w-5 h-5" />}
                    </button>
                  </div>
                  <p className="text-xs text-gray-400 mt-1">
                    Obtain this from IndiaMART Seller Panel &rarr; Lead Manager &rarr; CRM API
                  </p>
                </div>

                {/* Registered Mobile Number */}
                <div>
                  <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1.5">
                    IndiaMART Registered Primary Mobile Number
                  </label>
                  <input
                    type="text"
                    value={config.str_glusr_mobile}
                    onChange={(e) =>
                      setConfig((prev) => ({ ...prev, str_glusr_mobile: e.target.value }))
                    }
                    placeholder="e.g. 9876543210"
                    className="w-full px-3.5 py-2.5 rounded-xl border border-gray-200 dark:border-gray-700 bg-gray-50/50 dark:bg-gray-900/50 text-gray-900 dark:text-white text-sm focus:ring-2 focus:ring-[#3a57e8] focus:border-transparent transition-all"
                  />
                </div>
              </div>

              {/* 2. Real-time Inbound Webhook (Push API) Card */}
              <div className="bg-white dark:bg-gray-800 p-6 rounded-2xl shadow-sm border border-gray-100 dark:border-gray-700 space-y-4">
                <div className="flex items-center justify-between pb-3 border-b border-gray-100 dark:border-gray-700">
                  <div className="flex items-center gap-2.5">
                    <PaperAirplaneIcon className="w-5 h-5 text-indigo-500" />
                    <div>
                      <h3 className="text-base font-bold text-gray-900 dark:text-white">
                        Real-time Inbound Webhook (Push API)
                      </h3>
                      <p className="text-xs text-gray-500">
                        Paste this URL in IndiaMART to receive instant pushes when leads arrive
                      </p>
                    </div>
                  </div>
                </div>

                {/* Webhook URL Input with Copy & Regenerate */}
                <div>
                  <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1.5">
                    Your Tenant Webhook Callback URL
                  </label>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      readOnly
                      value={config.webhookUrl || "Generating webhook URL..."}
                      className="w-full px-3.5 py-2.5 rounded-xl border border-gray-200 dark:border-gray-700 bg-gray-100/70 dark:bg-gray-900/70 text-gray-700 dark:text-gray-300 text-xs font-mono select-all focus:outline-none"
                    />
                    <button
                      type="button"
                      onClick={handleCopyWebhook}
                      className="flex items-center gap-1.5 px-3.5 py-2.5 bg-[#3a57e8] hover:bg-[#2b44c2] text-white rounded-xl text-xs font-semibold transition-all shadow-sm flex-shrink-0 cursor-pointer"
                    >
                      {copiedWebhook ? (
                        <>
                          <ClipboardDocumentCheckIcon className="w-4 h-4 text-emerald-300" />
                          Copied!
                        </>
                      ) : (
                        <>
                          <ClipboardDocumentIcon className="w-4 h-4" />
                          Copy
                        </>
                      )}
                    </button>
                    <button
                      type="button"
                      onClick={handleRegenerateToken}
                      title="Regenerate Webhook URL"
                      className="px-3 py-2.5 border border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 rounded-xl text-xs font-semibold transition-all flex-shrink-0 cursor-pointer"
                    >
                      <ArrowPathIcon className="w-4 h-4" />
                    </button>
                  </div>
                </div>

                {/* How to setup in IndiaMART helper card */}
                <div className="p-3 bg-blue-50/70 dark:bg-blue-950/30 border border-blue-100 dark:border-blue-900/40 rounded-xl flex items-start gap-2.5">
                  <InformationCircleIcon className="w-5 h-5 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" />
                  <p className="text-xs text-blue-800 dark:text-blue-300 leading-relaxed">
                    <b>IndiaMART Push Setup:</b> Log in to your IndiaMART Seller Panel &rarr;
                    Lead Manager &rarr; CRM Settings &rarr; Add Webhook URL &rarr; Paste the URL above.
                    New leads will instantly trigger the automated WhatsApp response below.
                  </p>
                </div>
              </div>

              {/* 3. Automated WhatsApp "Thank You" Message Rules */}
              <div className="bg-white dark:bg-gray-800 p-6 rounded-2xl shadow-sm border border-gray-100 dark:border-gray-700 space-y-5">
                <div className="flex items-center justify-between pb-3 border-b border-gray-100 dark:border-gray-700">
                  <div className="flex items-center gap-2.5">
                    <ChatBubbleLeftRightIcon className="w-5 h-5 text-emerald-600" />
                    <div>
                      <h3 className="text-base font-bold text-gray-900 dark:text-white">
                        Automated WhatsApp "Thank You" Message
                      </h3>
                      <p className="text-xs text-gray-500">
                        Dynamic auto-responder sent to the buyer's phone upon lead arrival
                      </p>
                    </div>
                  </div>

                  {/* Auto-reply toggle */}
                  <label className="flex items-center gap-2 cursor-pointer">
                    <span className="text-xs font-medium text-gray-600 dark:text-gray-300">
                      Auto-Reply
                    </span>
                    <input
                      type="checkbox"
                      checked={config.bool_auto_reply_enabled}
                      onChange={(e) =>
                        setConfig((prev) => ({
                          ...prev,
                          bool_auto_reply_enabled: e.target.checked,
                        }))
                      }
                      className="sr-only peer"
                    />
                    <div className="w-10 h-6 bg-gray-200 peer-focus:outline-none rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-emerald-600" />
                  </label>
                </div>

                {/* WhatsApp Sender Number */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1.5">
                      Dispatching WhatsApp Sender Number
                    </label>
                    <select
                      value={config.str_sender_phone_number}
                      onChange={(e) =>
                        setConfig((prev) => ({
                          ...prev,
                          str_sender_phone_number: e.target.value,
                        }))
                      }
                      className="w-full px-3.5 py-2.5 rounded-xl border border-gray-200 dark:border-gray-700 bg-gray-50/50 dark:bg-gray-900/50 text-gray-900 dark:text-white text-sm focus:ring-2 focus:ring-[#3a57e8] focus:border-transparent transition-all"
                    >
                      <option value="">-- Use Default Account Number --</option>
                      {phoneNumbers.map((p, idx) => (
                        <option
                          key={p._id || idx}
                          value={p.str_phone_number || p.str_phone_number_id}
                        >
                          {p.str_phone_number || p.str_display_phone_number || p.str_phone_number_id}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Messaging Mode */}
                  <div>
                    <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1.5">
                      Message Dispatch Mode
                    </label>
                    <div className="flex gap-2">
                      <button
                        type="button"
                        onClick={() => setConfig((p) => ({ ...p, str_reply_mode: "TEXT" }))}
                        className={`flex-1 py-2 text-xs font-semibold rounded-xl border transition-all cursor-pointer ${
                          config.str_reply_mode === "TEXT"
                            ? "bg-[#3a57e8] text-white border-[#3a57e8] shadow-xs"
                            : "border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
                        }`}
                      >
                        Dynamic Custom Text
                      </button>
                      <button
                        type="button"
                        onClick={() =>
                          setConfig((p) => ({ ...p, str_reply_mode: "TEMPLATE" }))
                        }
                        className={`flex-1 py-2 text-xs font-semibold rounded-xl border transition-all cursor-pointer ${
                          config.str_reply_mode === "TEMPLATE"
                            ? "bg-[#3a57e8] text-white border-[#3a57e8] shadow-xs"
                            : "border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
                        }`}
                      >
                        Approved Template
                      </button>
                    </div>
                  </div>
                </div>

                {/* If Template Mode: Template Selector */}
                {config.str_reply_mode === "TEMPLATE" && (
                  <div className="space-y-3">
                    <div>
                      <div className="flex items-center justify-between mb-1.5">
                        <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300">
                          Select WhatsApp Approved Template
                        </label>
                        <span className="text-[11px] font-medium text-emerald-600 dark:text-emerald-400 flex items-center gap-1">
                          <CheckCircleIcon className="w-3.5 h-3.5" />
                          {templates.length} Approved Template{templates.length !== 1 ? "s" : ""} Available
                        </span>
                      </div>
                      <select
                        value={config.str_template_name}
                        onChange={(e) =>
                          setConfig((prev) => ({ ...prev, str_template_name: e.target.value }))
                        }
                        className="w-full px-3.5 py-2.5 rounded-xl border border-gray-200 dark:border-gray-700 bg-gray-50/50 dark:bg-gray-900/50 text-gray-900 dark:text-white text-sm focus:ring-2 focus:ring-[#3a57e8] focus:border-transparent transition-all"
                      >
                        <option value="">-- Select Template --</option>
                        {templates.map((t, idx) => {
                          const name = t.str_template_name || t.name;
                          const category = t.str_category || t.category || "UTILITY";
                          const lang = t.str_language || t.language || "en";
                          return (
                            <option key={t._id || t.int_template_id || idx} value={name}>
                              {name} ({category} · {lang})
                            </option>
                          );
                        })}
                      </select>
                      {templates.length === 0 && (
                        <p className="text-[11px] text-amber-600 dark:text-amber-400 mt-1">
                          ⚠️ No approved templates found. Go to <b>Message Templates</b> to sync or create templates.
                        </p>
                      )}
                    </div>

                    {/* Show selected template details preview box */}
                    {selectedTemplate && (
                      <div className="p-3.5 rounded-xl bg-gray-50 dark:bg-gray-900/60 border border-gray-200 dark:border-gray-700 text-xs space-y-2">
                        <div className="flex items-center justify-between">
                          <span className="font-semibold text-gray-800 dark:text-gray-200 flex items-center gap-1.5">
                            <span className="w-2 h-2 rounded-full bg-emerald-500" />
                            {selectedTemplate.str_template_name || selectedTemplate.name}
                          </span>
                          <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-emerald-100 text-emerald-800 dark:bg-emerald-950 dark:text-emerald-300">
                            {selectedTemplate.str_status || "APPROVED"}
                          </span>
                        </div>
                        {selectedTemplateBody ? (
                          <div className="text-gray-600 dark:text-gray-300 whitespace-pre-wrap font-sans bg-white dark:bg-gray-800 p-2.5 rounded-lg border border-gray-100 dark:border-gray-700 leading-relaxed text-[11px]">
                            {selectedTemplateBody}
                          </div>
                        ) : null}
                      </div>
                    )}
                  </div>
                )}

                {/* Custom Dynamic Message Textarea */}
                <div>
                  <div className="flex items-center justify-between mb-1.5">
                    <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300">
                      Dynamic Message Content
                    </label>
                    <span className="text-xs text-gray-400">
                      {(config.str_custom_message || "").length} characters
                    </span>
                  </div>

                  {/* Variable Tag Pills to click and insert */}
                  <div className="flex items-center flex-wrap gap-1.5 mb-2">
                    <span className="text-[11px] font-semibold text-gray-400 mr-1">Insert Variable:</span>
                    {["name", "phone", "product", "city", "company", "query_id"].map((tag) => (
                      <button
                        type="button"
                        key={tag}
                        onClick={() => handleInsertVariable(tag)}
                        className="px-2 py-0.5 rounded-lg text-xs font-medium bg-gray-100 hover:bg-[#ebf0fe] hover:text-[#3a57e8] text-gray-600 dark:bg-gray-700 dark:text-gray-300 transition-colors cursor-pointer"
                      >
                        + {`{{${tag}}}`}
                      </button>
                    ))}
                  </div>

                  <textarea
                    rows={5}
                    value={config.str_custom_message}
                    onChange={(e) =>
                      setConfig((prev) => ({ ...prev, str_custom_message: e.target.value }))
                    }
                    placeholder="Write your dynamic message here..."
                    className="w-full p-3.5 rounded-xl border border-gray-200 dark:border-gray-700 bg-gray-50/50 dark:bg-gray-900/50 text-gray-900 dark:text-white text-sm focus:ring-2 focus:ring-[#3a57e8] focus:border-transparent transition-all leading-relaxed"
                  />
                </div>

                {/* 24h Duplicate Prevention Checkbox */}
                <div className="pt-1">
                  <label className="flex items-center gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={config.bool_prevent_duplicate_24h}
                      onChange={(e) =>
                        setConfig((prev) => ({
                          ...prev,
                          bool_prevent_duplicate_24h: e.target.checked,
                        }))
                      }
                      className="w-4 h-4 text-[#3a57e8] rounded border-gray-300 focus:ring-[#3a57e8]"
                    />
                    <div>
                      <span className="text-xs font-bold text-gray-800 dark:text-gray-200">
                        24-Hour Anti-Spam Deduplication
                      </span>
                      <p className="text-[11px] text-gray-500">
                        Do not dispatch an auto-reply if the same buyer submitted multiple inquiries in the last 24 hours
                      </p>
                    </div>
                  </label>
                </div>
              </div>

              {/* Submit Button */}
              <div className="flex justify-end">
                <button
                  type="submit"
                  disabled={savingConfig}
                  className="px-6 py-2.5 bg-[#3a57e8] hover:bg-[#2b44c2] text-white rounded-xl text-sm font-semibold transition-all shadow-md shadow-[#3a57e8]/30 flex items-center gap-2 cursor-pointer disabled:opacity-60"
                >
                  <CheckCircleIcon className="w-5 h-5" />
                  {savingConfig ? "Saving Configuration..." : "Save Settings"}
                </button>
              </div>
            </form>
          </div>

          {/* Right Column: Live WhatsApp Message Simulator (5 cols) */}
          <div className="lg:col-span-5 space-y-6">
            <div className="bg-white dark:bg-gray-800 p-6 rounded-2xl shadow-sm border border-gray-100 dark:border-gray-700 sticky top-6">
              <div className="flex items-center gap-2 pb-3 border-b border-gray-100 dark:border-gray-700 mb-4">
                <PhoneIcon className="w-5 h-5 text-emerald-600" />
                <h3 className="text-sm font-bold text-gray-900 dark:text-white uppercase tracking-wider">
                  Live WhatsApp Message Simulator
                </h3>
              </div>

              {/* Realistic Phone Shell */}
              <div className="bg-[#e5ddd5] dark:bg-[#0b141a] rounded-2xl p-4 shadow-inner min-h-[380px] flex flex-col justify-between border border-gray-200 dark:border-gray-700">
                {/* Chat Header */}
                <div className="bg-[#075e54] dark:bg-[#202c33] text-white p-3 rounded-xl flex items-center gap-3 shadow-sm">
                  <div className="w-9 h-9 rounded-full bg-emerald-700 flex items-center justify-center font-bold text-sm">
                    {config.str_sender_phone_number?.slice(-2) || "DF"}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-xs font-bold truncate">
                      {currentUser?.str_name || "Your Business Name"}
                    </div>
                    <div className="text-[10px] text-emerald-200 flex items-center gap-1">
                      <span className="w-1.5 h-1.5 rounded-full bg-emerald-400" />
                      Official Business Account
                    </div>
                  </div>
                </div>

                {/* Message Bubble */}
                <div className="my-auto py-4">
                  <div className="max-w-[88%] ml-auto bg-[#d9fdd3] dark:bg-[#005c4b] text-gray-900 dark:text-gray-100 p-3.5 rounded-2xl rounded-tr-xs shadow-sm text-xs leading-relaxed whitespace-pre-wrap">
                    {previewMessage || "Start typing your auto-reply message..."}
                    <div className="text-[10px] text-gray-500 dark:text-gray-300 text-right mt-1 flex items-center justify-end gap-1 font-mono">
                      <span>Just now</span>
                      <CheckIcon className="w-3.5 h-3.5 text-blue-500" />
                    </div>
                  </div>
                </div>

                {/* Simulation Notice */}
                <div className="text-center text-[11px] text-gray-600 dark:text-gray-400 py-1 bg-white/70 dark:bg-black/30 backdrop-blur-xs rounded-lg">
                  Preview interpolated with sample lead: <b>Rahul Sharma (Ahmedabad)</b>
                </div>
              </div>

              {/* Status info */}
              <div className="mt-4 pt-4 border-t border-gray-100 dark:border-gray-700 space-y-2 text-xs text-gray-500">
                <div className="flex justify-between">
                  <span>Last CRM Sync:</span>
                  <span className="font-semibold text-gray-700 dark:text-gray-300">
                    {config.dttm_last_sync_on
                      ? new Date(config.dttm_last_sync_on).toLocaleString()
                      : "Never"}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span>Total Leads Ingested:</span>
                  <span className="font-semibold text-gray-700 dark:text-gray-300">
                    {config.int_total_leads || 0}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span>Total Replies Dispatched:</span>
                  <span className="font-semibold text-emerald-600 dark:text-emerald-400">
                    {config.int_total_sent || 0}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
        )
      )}

      {/* ════════════════════════════════════════════════════════════════════════ */}
      {/* TAB 2: LEADS HISTORY & MESSAGE AUDIT LOGS                                */}
      {/* ════════════════════════════════════════════════════════════════════════ */}
      {activeTab === "leads" && (
        <div className="space-y-6">
          {/* Metric Summary Cards */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="bg-white dark:bg-gray-800 p-5 rounded-2xl border border-gray-100 dark:border-gray-700 shadow-sm flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-blue-50 dark:bg-blue-950/50 text-[#3a57e8] flex items-center justify-center font-bold">
                📥
              </div>
              <div>
                <div className="text-2xl font-bold text-gray-900 dark:text-white">
                  {leadsStats.totalAll}
                </div>
                <div className="text-xs text-gray-500 font-medium">Total Inquiries</div>
              </div>
            </div>

            <div className="bg-white dark:bg-gray-800 p-5 rounded-2xl border border-gray-100 dark:border-gray-700 shadow-sm flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-emerald-50 dark:bg-emerald-950/50 text-emerald-600 flex items-center justify-center font-bold">
                💬
              </div>
              <div>
                <div className="text-2xl font-bold text-emerald-600 dark:text-emerald-400">
                  {leadsStats.totalSent}
                </div>
                <div className="text-xs text-gray-500 font-medium">Auto-Replies Sent</div>
              </div>
            </div>

            <div className="bg-white dark:bg-gray-800 p-5 rounded-2xl border border-gray-100 dark:border-gray-700 shadow-sm flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-rose-50 dark:bg-rose-950/50 text-rose-600 flex items-center justify-center font-bold">
                ⚠️
              </div>
              <div>
                <div className="text-2xl font-bold text-rose-600 dark:text-rose-400">
                  {leadsStats.totalFailed}
                </div>
                <div className="text-xs text-gray-500 font-medium">Dispatches Failed</div>
              </div>
            </div>

            <div className="bg-white dark:bg-gray-800 p-5 rounded-2xl border border-gray-100 dark:border-gray-700 shadow-sm flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-gray-50 dark:bg-gray-700/50 text-gray-600 dark:text-gray-300 flex items-center justify-center font-bold">
                ⏭️
              </div>
              <div>
                <div className="text-2xl font-bold text-gray-700 dark:text-gray-300">
                  {leadsStats.totalSkipped}
                </div>
                <div className="text-xs text-gray-500 font-medium">Duplicates / Skipped</div>
              </div>
            </div>
          </div>

          {/* Filter Toolbar */}
          <div className="bg-white dark:bg-gray-800 p-4 rounded-2xl border border-gray-100 dark:border-gray-700 shadow-sm flex flex-col sm:flex-row items-center justify-between gap-3">
            {/* Search Input */}
            <div className="relative w-full sm:w-80">
              <MagnifyingGlassIcon className="w-4 h-4 text-gray-400 absolute left-3.5 top-3" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search by buyer, phone, product..."
                className="w-full pl-9 pr-3.5 py-2 rounded-xl border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900 text-xs focus:ring-2 focus:ring-[#3a57e8]"
              />
            </div>

            {/* Filters & Export */}
            <div className="flex items-center flex-wrap gap-2 w-full sm:w-auto justify-end">
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="px-3 py-2 rounded-xl border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900 text-xs font-semibold text-gray-700 dark:text-gray-300"
              >
                <option value="ALL">All Statuses</option>
                <option value="SENT">Sent</option>
                <option value="FAILED">Failed</option>
                <option value="SKIPPED">Skipped (24h Duplicate)</option>
                <option value="PENDING">Pending</option>
              </select>

              <input
                type="date"
                value={dateFilter}
                onChange={(e) => setDateFilter(e.target.value)}
                className="px-3 py-2 rounded-xl border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900 text-xs font-semibold text-gray-700 dark:text-gray-300"
                title="Filter by inquiry date"
              />

              <button
                onClick={() => loadLeads(1)}
                className="p-2 rounded-xl border border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-600 dark:text-gray-300 cursor-pointer"
                title="Refresh Table"
              >
                <ArrowPathIcon className="w-4 h-4" />
              </button>

              <button
                onClick={exportToCSV}
                className="flex items-center gap-1.5 px-3 py-2 rounded-xl border border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 text-xs font-semibold cursor-pointer"
              >
                <ArrowDownTrayIcon className="w-4 h-4 text-[#3a57e8]" />
                Export CSV
              </button>
            </div>
          </div>

          {/* Leads Table */}
          <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700 shadow-sm overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-left border-collapse text-xs">
                <thead>
                  <tr className="bg-gray-50 dark:bg-gray-900/60 text-gray-500 dark:text-gray-400 border-b border-gray-100 dark:border-gray-700 font-semibold uppercase tracking-wider text-[11px]">
                    <th className="py-3 px-4">Date & Time</th>
                    <th className="py-3 px-4">Buyer Details</th>
                    <th className="py-3 px-4">Product / Requirement</th>
                    <th className="py-3 px-4">Source</th>
                    <th className="py-3 px-4">WhatsApp Status</th>
                    <th className="py-3 px-4 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
                  {loadingLeads ? (
                    <tr>
                      <td colSpan={6} className="py-12 text-center text-gray-400">
                        <ArrowPathIcon className="w-6 h-6 animate-spin mx-auto mb-2 text-[#3a57e8]" />
                        Loading IndiaMART leads...
                      </td>
                    </tr>
                  ) : leads.length === 0 ? (
                    <tr>
                      <td colSpan={6} className="py-12 text-center text-gray-400">
                        No leads found matching your criteria. Click "Fetch Leads Now" to pull from IndiaMART.
                      </td>
                    </tr>
                  ) : (
                    leads.map((lead) => (
                      <tr
                        key={lead._id}
                        className="hover:bg-gray-50/60 dark:hover:bg-gray-900/40 transition-colors"
                      >
                        {/* Date Time */}
                        <td className="py-3 px-4 whitespace-nowrap text-gray-500 font-mono">
                          {new Date(lead.dttm_query_time || lead.dttm_created_on).toLocaleString([], {
                            dateStyle: "medium",
                            timeStyle: "short",
                          })}
                          <div className="text-[10px] text-gray-400 font-mono">
                            ID: {lead.str_query_id?.slice(-8) || "N/A"}
                          </div>
                        </td>

                        {/* Buyer Info */}
                        <td className="py-3 px-4">
                          <div className="font-bold text-gray-900 dark:text-white">
                            {lead.str_sender_name || "Unknown Buyer"}
                          </div>
                          <div className="text-gray-600 dark:text-gray-300 font-mono">
                            {lead.str_sender_mobile || "No Mobile"}
                          </div>
                          {lead.str_sender_city && (
                            <div className="text-[10px] text-gray-400">
                              📍 {lead.str_sender_city}
                              {lead.str_sender_state ? `, ${lead.str_sender_state}` : ""}
                            </div>
                          )}
                        </td>

                        {/* Product / Requirement */}
                        <td className="py-3 px-4 max-w-xs">
                          <div className="font-semibold text-gray-900 dark:text-white truncate" title={lead.str_product_name || lead.str_subject}>
                            {lead.str_product_name || lead.str_subject || "General Inquiry"}
                          </div>
                          {lead.str_query_message && (
                            <div className="text-gray-500 dark:text-gray-400 line-clamp-1 text-[11px]" title={lead.str_query_message}>
                              "{lead.str_query_message}"
                            </div>
                          )}
                        </td>

                        {/* Source */}
                        <td className="py-3 px-4 whitespace-nowrap">
                          <span className="px-2 py-0.5 rounded-md text-[10px] font-bold bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300">
                            {lead.str_source || "WEBHOOK"}
                          </span>
                        </td>

                        {/* WhatsApp Status Badge */}
                        <td className="py-3 px-4 whitespace-nowrap">
                          {lead.str_reply_status === "SENT" && (
                            <span
                              className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-bold bg-emerald-50 text-emerald-700 border border-emerald-200 dark:bg-emerald-950/40 dark:text-emerald-400"
                              title={lead.str_reply_message}
                            >
                              <CheckCircleIcon className="w-3.5 h-3.5" />
                              Sent
                            </span>
                          )}

                          {lead.str_reply_status === "FAILED" && (
                            <span
                              className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-bold bg-rose-50 text-rose-700 border border-rose-200 dark:bg-rose-950/40 dark:text-rose-400"
                              title={lead.str_reply_error}
                            >
                              <ExclamationTriangleIcon className="w-3.5 h-3.5" />
                              Failed
                            </span>
                          )}

                          {lead.str_reply_status === "SKIPPED" && (
                            <span
                              className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-semibold bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300"
                              title={lead.str_reply_error || "Duplicate 24h inquiry"}
                            >
                              Skipped
                            </span>
                          )}

                          {lead.str_reply_status === "PENDING" && (
                            <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-semibold bg-amber-50 text-amber-700 border border-amber-200">
                              <ClockIcon className="w-3.5 h-3.5" />
                              Pending
                            </span>
                          )}
                        </td>

                        {/* Actions */}
                        <td className="py-3 px-4 text-right whitespace-nowrap space-x-1.5">
                          {/* Send / Resend WhatsApp */}
                          <button
                            onClick={() => openManualSend(lead)}
                            className="p-1.5 rounded-lg text-emerald-600 hover:bg-emerald-50 dark:hover:bg-emerald-950/30 transition-colors"
                            title="Send WhatsApp Message"
                          >
                            <PaperAirplaneIcon className="w-4 h-4" />
                          </button>

                          {/* Open in Live Chat */}
                          <button
                            onClick={() => {
                              const clean = (lead.str_sender_phone_clean || lead.str_sender_mobile || "").replace(/\D/g, "");
                              navigate(`/workspace/chat?phone=${clean}`);
                            }}
                            className="p-1.5 rounded-lg text-[#3a57e8] hover:bg-[#ebf0fe] dark:hover:bg-gray-700 transition-colors"
                            title="Open in Live Chat"
                          >
                            <ChatBubbleLeftRightIcon className="w-4 h-4" />
                          </button>

                          {/* View Lead Details */}
                          <button
                            onClick={() => {
                              setSelectedLead(lead);
                              setShowDetailModal(true);
                            }}
                            className="p-1.5 rounded-lg text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                            title="View Full Lead Details"
                          >
                            <InformationCircleIcon className="w-4 h-4" />
                          </button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            {/* Pagination footer */}
            {totalPages > 1 && (
              <div className="p-4 border-t border-gray-100 dark:border-gray-800 flex items-center justify-between text-xs text-gray-500">
                <span>
                  Page <b>{page}</b> of <b>{totalPages}</b>
                </span>
                <div className="flex gap-1.5">
                  <button
                    disabled={page <= 1}
                    onClick={() => loadLeads(page - 1)}
                    className="px-3 py-1.5 rounded-lg border border-gray-200 dark:border-gray-700 disabled:opacity-40 hover:bg-gray-50 dark:hover:bg-gray-700 font-semibold"
                  >
                    Previous
                  </button>
                  <button
                    disabled={page >= totalPages}
                    onClick={() => loadLeads(page + 1)}
                    className="px-3 py-1.5 rounded-lg border border-gray-200 dark:border-gray-700 disabled:opacity-40 hover:bg-gray-50 dark:hover:bg-gray-700 font-semibold"
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── MODAL 1: Manual Send WhatsApp Modal ── */}
      {showManualModal && selectedLead && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-xs">
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl max-w-lg w-full p-6 border border-gray-100 dark:border-gray-700 space-y-4 animate-in fade-in zoom-in duration-150">
            <div className="flex items-center justify-between pb-3 border-b border-gray-100 dark:border-gray-700">
              <div className="flex items-center gap-2">
                <PaperAirplaneIcon className="w-5 h-5 text-emerald-600" />
                <h3 className="text-base font-bold text-gray-900 dark:text-white">
                  Send WhatsApp Message
                </h3>
              </div>
              <button
                onClick={() => setShowManualModal(false)}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
              >
                <XMarkIcon className="w-5 h-5" />
              </button>
            </div>

            {/* Recipient summary */}
            <div className="p-3 bg-gray-50 dark:bg-gray-900 rounded-xl text-xs space-y-1">
              <div>
                <span className="text-gray-500">Recipient:</span>{" "}
                <b>{selectedLead.str_sender_name}</b> ({selectedLead.str_sender_mobile})
              </div>
              <div>
                <span className="text-gray-500">Inquiry:</span>{" "}
                <b>{selectedLead.str_product_name || selectedLead.str_subject}</b>
              </div>
            </div>

            {/* Editable Message text */}
            <div>
              <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1.5">
                Message Content
              </label>
              <textarea
                rows={5}
                value={manualMessage}
                onChange={(e) => setManualMessage(e.target.value)}
                className="w-full p-3 rounded-xl border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900 text-xs text-gray-900 dark:text-white leading-relaxed focus:ring-2 focus:ring-[#3a57e8]"
              />
            </div>

            {/* Buttons */}
            <div className="flex justify-end gap-2 pt-2">
              <button
                type="button"
                onClick={() => setShowManualModal(false)}
                className="px-4 py-2 rounded-xl text-xs font-semibold text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
              >
                Cancel
              </button>
              <button
                type="button"
                disabled={manualSending || !manualMessage.trim()}
                onClick={submitManualSend}
                className="px-5 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl text-xs font-semibold transition-all shadow-md shadow-emerald-600/30 flex items-center gap-2 cursor-pointer disabled:opacity-50"
              >
                <PaperAirplaneIcon className="w-4 h-4" />
                {manualSending ? "Sending..." : "Dispatch Now"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── MODAL 2: Full Lead Details Modal ── */}
      {showDetailModal && selectedLead && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-xs">
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl max-w-xl w-full p-6 border border-gray-100 dark:border-gray-700 space-y-4 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between pb-3 border-b border-gray-100 dark:border-gray-700">
              <h3 className="text-base font-bold text-gray-900 dark:text-white">
                IndiaMART Lead Details
              </h3>
              <button
                onClick={() => setShowDetailModal(false)}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
              >
                <XMarkIcon className="w-5 h-5" />
              </button>
            </div>

            <div className="grid grid-cols-2 gap-3 text-xs">
              <div className="p-3 bg-gray-50 dark:bg-gray-900 rounded-xl">
                <span className="text-gray-400 block mb-0.5">Unique Query ID</span>
                <span className="font-mono font-bold text-gray-900 dark:text-white">
                  {selectedLead.str_query_id}
                </span>
              </div>
              <div className="p-3 bg-gray-50 dark:bg-gray-900 rounded-xl">
                <span className="text-gray-400 block mb-0.5">Query Time</span>
                <span className="font-bold text-gray-900 dark:text-white">
                  {new Date(selectedLead.dttm_query_time).toLocaleString()}
                </span>
              </div>
              <div className="p-3 bg-gray-50 dark:bg-gray-900 rounded-xl">
                <span className="text-gray-400 block mb-0.5">Buyer Name</span>
                <span className="font-bold text-gray-900 dark:text-white">
                  {selectedLead.str_sender_name}
                </span>
              </div>
              <div className="p-3 bg-gray-50 dark:bg-gray-900 rounded-xl">
                <span className="text-gray-400 block mb-0.5">Phone Number</span>
                <span className="font-bold font-mono text-gray-900 dark:text-white">
                  {selectedLead.str_sender_mobile}
                </span>
              </div>
              <div className="p-3 bg-gray-50 dark:bg-gray-900 rounded-xl col-span-2">
                <span className="text-gray-400 block mb-0.5">Product / Subject</span>
                <span className="font-bold text-gray-900 dark:text-white">
                  {selectedLead.str_product_name || selectedLead.str_subject}
                </span>
              </div>
              <div className="p-3 bg-gray-50 dark:bg-gray-900 rounded-xl col-span-2">
                <span className="text-gray-400 block mb-0.5">Message / Requirement</span>
                <p className="text-gray-800 dark:text-gray-200 whitespace-pre-wrap">
                  {selectedLead.str_query_message || "No requirement message specified."}
                </p>
              </div>
              {selectedLead.str_reply_message && (
                <div className="p-3 bg-emerald-50 dark:bg-emerald-950/30 rounded-xl col-span-2 border border-emerald-100 dark:border-emerald-900/40">
                  <span className="text-emerald-700 dark:text-emerald-300 font-bold block mb-0.5">
                    WhatsApp Message Sent
                  </span>
                  <p className="text-emerald-950 dark:text-emerald-100 whitespace-pre-wrap">
                    {selectedLead.str_reply_message}
                  </p>
                </div>
              )}
            </div>

            {/* Raw JSON toggle */}
            <details className="text-xs">
              <summary className="cursor-pointer text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 font-semibold">
                Inspect Raw IndiaMART Payload
              </summary>
              <pre className="mt-2 p-3 bg-gray-900 text-emerald-400 rounded-xl overflow-x-auto text-[11px] font-mono max-h-48">
                {JSON.stringify(selectedLead.raw_payload || {}, null, 2)}
              </pre>
            </details>
          </div>
        </div>
      )}
    </div>
  );
}
